import DOMPurify from "dompurify";
import type { JoinPageHosts } from "../../types/customJoinPage";

/**
 * Builds the document that goes into the sandboxed iframe's `srcdoc`.
 *
 * Security model (see custom-join-page-plan.md §3–4): the iframe has
 * `sandbox="allow-scripts"` and nothing else, so the document runs in an
 * opaque origin with no cookies, storage, parent DOM or Reveshare API. The
 * meta CSP below closes network egress to anything but Reveshare storage and
 * the font hosts, and only allows the one script we inject (nonce). The
 * brand's HTML has already been through the server linter; DOMPurify runs a
 * second time here with the browser's own parser.
 */

export const RS_MESSAGE = {
  READY: "RS_READY",
  ACTION: "RS_ACTION",
  NAVIGATE: "RS_NAVIGATE",
  HEIGHT: "RS_HEIGHT",
  STATE: "RS_STATE",
} as const;

/** Tags the server never lets through; forbidding them here too costs nothing. */
const FORBID_TAGS = [
  "script", "style", "link", "meta", "base", "iframe", "frame", "frameset",
  "object", "embed", "applet", "form", "input", "textarea", "select", "option",
  "video", "audio", "track", "canvas", "noscript", "template", "slot",
  "foreignObject", "use", "animate", "animateTransform", "animateMotion", "set",
];

export function purifyFragment(html: string): string {
  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true, svg: true, svgFilters: true },
    FORBID_TAGS,
    FORBID_ATTR: ["srcdoc", "formaction", "xlink:href"],
    ALLOW_DATA_ATTR: true,
    ADD_ATTR: ["loading", "decoding"],
    // Only https and inline images; the server already enforced hosts.
    ALLOWED_URI_REGEXP: /^(?:https:|data:image\/|#)/i,
  });
}

/** The only script that runs inside the frame. ES5 on purpose. */
export const BRIDGE_SCRIPT = `(function () {
  var post = function (m) { parent.postMessage(m, '*'); };
  document.addEventListener('click', function (e) {
    var t = e.target;
    while (t && t !== document.documentElement) {
      if (t.getAttribute && t.getAttribute('data-rs-action')) {
        e.preventDefault();
        post({ type: '${RS_MESSAGE.ACTION}', action: t.getAttribute('data-rs-action') });
        return;
      }
      if (t.tagName === 'A') {
        e.preventDefault();
        var h = t.getAttribute('href') || '';
        if (h.charAt(0) === '#') {
          var el = h.length > 1 ? document.getElementById(h.slice(1)) : null;
          if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth' });
          return;
        }
        if (h) post({ type: '${RS_MESSAGE.NAVIGATE}', href: h });
        return;
      }
      t = t.parentNode;
    }
  }, true);
  var last = -1;
  var report = function () {
    var h = Math.max(document.documentElement.scrollHeight, document.body ? document.body.scrollHeight : 0);
    if (h !== last) { last = h; post({ type: '${RS_MESSAGE.HEIGHT}', px: h }); }
  };
  if (window.ResizeObserver) {
    var ro = new ResizeObserver(report);
    ro.observe(document.documentElement);
    if (document.body) ro.observe(document.body);
  } else {
    setInterval(report, 500);
  }
  window.addEventListener('load', report);
  var applyState = function (state) {
    document.documentElement.setAttribute('data-rs-state', state);
    var els = document.querySelectorAll('[data-rs-action="join"]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (!el.hasAttribute('data-rs-label-default')) el.setAttribute('data-rs-label-default', el.textContent);
      var label = el.getAttribute('data-rs-label-' + state);
      el.textContent = label !== null ? label : el.getAttribute('data-rs-label-default');
      if (state === 'loading') el.setAttribute('aria-busy', 'true'); else el.removeAttribute('aria-busy');
    }
  };
  window.addEventListener('message', function (e) {
    var d = e.data || {};
    if (d.type === '${RS_MESSAGE.STATE}' && typeof d.state === 'string') applyState(d.state);
  });
  report();
  post({ type: '${RS_MESSAGE.READY}' });
})();`;

export interface FrameDocumentInput {
  html: string;
  css: string;
  hosts: JoinPageHosts;
  nonce: string;
  /** Brand colours exposed as --rs-* custom properties. */
  cssVars?: Partial<Record<"primary" | "secondary" | "fg" | "bg", string | null | undefined>>;
}

const COLOR_RE = /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%/]+\)|hsla?\([\d\s.,%/deg]+\))$/i;

function safeColor(v: string | null | undefined, fallback: string): string {
  const s = (v ?? "").trim();
  return COLOR_RE.test(s) ? s : fallback;
}

export function buildContentSecurityPolicy(hosts: JoinPageHosts, nonce: string): string {
  const origins = (list: string[]) => list.map((h) => `https://${h}`).join(" ");
  return [
    "default-src 'none'",
    `img-src ${origins(hosts.image)} data:`,
    `style-src 'unsafe-inline' ${origins(hosts.style)}`,
    `font-src ${origins(hosts.font)}`,
    `script-src 'nonce-${nonce}'`,
    "form-action 'none'",
    "base-uri 'none'",
    "object-src 'none'",
  ].join("; ");
}

export function buildFrameDocument(input: FrameDocumentInput): string {
  const { hosts, nonce } = input;
  const vars = {
    primary: safeColor(input.cssVars?.primary, "#7C3AED"),
    secondary: safeColor(input.cssVars?.secondary, "#EDE9FE"),
    fg: safeColor(input.cssVars?.fg, "#111827"),
    bg: safeColor(input.cssVars?.bg, "#FFFFFF"),
  };
  const body = purifyFragment(input.html);
  // A closing </style> inside the stylesheet would end our element early;
  // the linter's parser rejects it, but escape anyway.
  const css = input.css.replace(/<\/style/gi, "<\\/style");

  return [
    "<!doctype html>",
    '<html lang="en">',
    "<head>",
    '<meta charset="utf-8">',
    `<meta http-equiv="Content-Security-Policy" content="${buildContentSecurityPolicy(hosts, nonce)}">`,
    '<meta name="viewport" content="width=device-width, initial-scale=1">',
    `<style>:root{--rs-primary:${vars.primary};--rs-secondary:${vars.secondary};--rs-fg:${vars.fg};--rs-bg:${vars.bg}}html,body{margin:0}img{max-width:100%}[data-rs-action]{cursor:pointer}</style>`,
    `<style>${css}</style>`,
    "</head>",
    "<body>",
    body,
    `<script nonce="${nonce}">${BRIDGE_SCRIPT}</script>`,
    "</body>",
    "</html>",
  ].join("\n");
}

export function randomNonce(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}
